<template>
  <div v-show="agentSetup.currentPage === 8" class="bg-base-100 border border-base-300 rounded-box p-6 shadow-sm" style="min-height: 40vh;">
    <div class="flex justify-between items-center" id="tabTitle">
      <h1 class="text-2xl mb-0" :style="agentSetup.isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.tabs.agentStyle', 'Agent Style') }}</h1>
    </div>
    <div role="tablist" class="tabs tabs-lifted mt-4">
      <input type="radio" name="agent_style_light_tabs" role="tab" class="tab" :aria-label="t('agentStyles.tabs.configuration', 'Configuration')" :checked="styleTab === 'configuration'" @click="styleTab = 'configuration'" />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-5 md:p-6">
        <div class="light-style-stack">
          <div class="light-style-section">
            <div class="light-style-section-title">{{ t('agentStyles.lightSetup.colorsTitle', 'Brand Colors') }}</div>
            <div class="light-style-row">
              <div>
                <label class="label">
                  <span class="label-text">{{ t('agentStyles.primaryColorLabel', 'Primary Color') }}</span>
                </label>
                <div class="light-style-color-field">
                  <input v-model="agentSetup.agentStyleDraft.primaryColor" type="color" class="light-style-color-input" />
                  <input v-model="agentSetup.agentStyleDraft.primaryColor" type="text" class="input input-bordered w-full" maxlength="20" autocomplete="off" autocapitalize="none" :spellcheck="false" />
                </div>
              </div>
              <div>
                <label class="label">
                  <span class="label-text">{{ t('agentStyles.secondaryColorLabel', 'Secondary Color') }}</span>
                </label>
                <div class="light-style-color-field">
                  <input v-model="agentSetup.agentStyleDraft.secondaryColor" type="color" class="light-style-color-input" />
                  <input v-model="agentSetup.agentStyleDraft.secondaryColor" type="text" class="input input-bordered w-full" maxlength="20" autocomplete="off" autocapitalize="none" :spellcheck="false" />
                </div>
              </div>
            </div>
          </div>

          <div class="light-style-section">
            <div class="light-style-section-title">{{ t('agentStyles.lightSetup.iconsTitle', 'Icons') }}</div>
            <div class="light-style-row">
              <div>
                <label class="label">
                  <span class="label-text">{{ t('agentStyles.logoLabel', 'Logo') }}</span>
                </label>
                <div class="light-style-upload" @click="logoFileInput !== null ? logoFileInput.click() : null" @dragenter.prevent="isDraggingLogo = true" @dragleave.prevent="isDraggingLogo = false" @dragover.prevent @drop.prevent="handleLogoDrop" :class="isDraggingLogo === true ? 'border-primary bg-primary/5' : ''">
                  <div class="light-style-upload-inner">
                    <svg class="w-7 h-7 text-base-content/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                    <div class="text-sm font-medium">{{ t('agentStyles.lightSetup.uploadLogo', 'Upload logo') }}</div>
                    <div class="text-xs text-base-content/60">{{ t('agentStyles.logoHint', 'SVG, PNG, JPG, GIF (Max 5MB)') }}</div>
                  </div>
                  <input ref="logoFileInput" type="file" class="hidden" accept="image/svg+xml, image/png, image/jpeg, image/gif" @change="handleLogoUpload" />
                </div>
                <div v-if="agentSetup.agentStyleDraft.logobase64 !== ''" class="light-style-preview">
                  <object v-if="typeof agentSetup.agentStyleDraft.logobase64 === 'string' && agentSetup.agentStyleDraft.logobase64.indexOf('data:image/svg+xml') === 0" :data="agentSetup.agentStyleDraft.logobase64" type="image/svg+xml" class="light-style-preview-image" aria-hidden="true" tabindex="-1"></object>
                  <img v-else :src="agentSetup.agentStyleDraft.logobase64" alt="Logo preview" class="light-style-preview-image" />
                  <button type="button" class="btn btn-xs btn-ghost text-error" @click.stop="removeLogo">{{ t('common.remove', 'Remove') }}</button>
                </div>
              </div>
              <div>
                <label class="label">
                  <span class="label-text">{{ t('agentStyles.chatIconLabel', 'Chat Icon') }}</span>
                </label>
                <div class="light-style-upload" @click="chatIconFileInput !== null ? chatIconFileInput.click() : null" @dragenter.prevent="isDraggingIcon = true" @dragleave.prevent="isDraggingIcon = false" @dragover.prevent @drop.prevent="handleChatIconDrop" :class="isDraggingIcon === true ? 'border-primary bg-primary/5' : ''">
                  <div class="light-style-upload-inner">
                    <svg class="w-7 h-7 text-base-content/40" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 0 1-.88-7.903A5 5 0 1 1 15.9 6L16 6a5 5 0 0 1 1 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                    <div class="text-sm font-medium">{{ t('agentStyles.lightSetup.uploadChatIcon', 'Upload chat icon') }}</div>
                    <div class="text-xs text-base-content/60">{{ t('agentStyles.iconHint', 'SVG, PNG, or GIF (Max 1MB)') }}</div>
                  </div>
                  <input ref="chatIconFileInput" type="file" class="hidden" accept="image/svg+xml, image/png, image/gif" @change="handleChatIconUpload" />
                </div>
                <div v-if="agentSetup.agentStyleDraft.chatIconBase64 !== ''" class="light-style-preview">
                  <img :src="agentSetup.agentStyleDraft.chatIconBase64" alt="Chat icon preview" class="light-style-preview-image" />
                  <button type="button" class="btn btn-xs btn-ghost text-error" @click.stop="removeChatIcon">{{ t('common.remove', 'Remove') }}</button>
                </div>
              </div>
            </div>
          </div>

          <div class="light-style-section">
            <div class="light-style-section-title">{{ t('agentStyles.sections.chat', 'Chat') }}</div>
            <div class="light-style-stack">
              <div>
                <label class="label">
                  <span class="label-text">{{ t('agentStyles.chatInputPlaceholderLabel', 'Chat Input Placeholder') }}</span>
                </label>
                <input v-model="agentSetup.agentStyleDraft.chatInputPlaceholder" type="text" class="input input-bordered w-full" :placeholder="t('agentStyles.chatInputPlaceholderPlaceholder', 'e.g., Type your question here...')" maxlength="120" autocomplete="off" autocapitalize="none" :spellcheck="false" />
              </div>

              <div class="light-style-toggle-card">
                <div class="light-style-toggle-header">
                  <div>
                    <div class="font-medium">{{ t('agentStyles.showMessageUnderTextbox', 'Show message under chat input') }}</div>
                    <div class="text-xs text-base-content/60 mt-1">{{ t('agentStyles.lightSetup.helperTextDescription', 'Show a short helper line below the message box.') }}</div>
                  </div>
                  <input v-model="agentSetup.agentStyleDraft.showMessageUnderTextbox" type="checkbox" class="toggle toggle-primary" />
                </div>
                <div v-if="agentSetup.agentStyleDraft.showMessageUnderTextbox === true" class="light-style-toggle-body">
                  <label class="label">
                    <span class="label-text">{{ t('agentStyles.placeholders.messageUnderTextbox', 'Enter message under chat input') }}</span>
                  </label>
                  <input v-model="agentSetup.agentStyleDraft.messageUnderTextbox" type="text" class="input input-bordered w-full" :placeholder="t('agentStyles.placeholders.messageUnderTextbox', 'Enter message under chat input')" maxlength="200" autocomplete="off" autocapitalize="none" :spellcheck="false" />
                </div>
              </div>

              <div class="light-style-toggle-card">
                <div class="light-style-toggle-header">
                  <div>
                    <div class="font-medium">{{ t('agentStyles.showTermsModal', 'Show terms modal before chat') }}</div>
                    <div class="text-xs text-base-content/60 mt-1">{{ t('agentStyles.lightSetup.termsDescription', 'Turn this on to ask users to approve a short terms message before chatting.') }}</div>
                  </div>
                  <input v-model="agentSetup.agentStyleDraft.showTermsModal" type="checkbox" class="toggle toggle-primary" />
                </div>
                <div v-if="agentSetup.agentStyleDraft.showTermsModal === true" class="light-style-toggle-body">
                  <div>
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.termsButtonTextLabel', 'Terms Button Text') }}</span>
                    </label>
                    <input v-model="agentSetup.agentStyleDraft.termsButtonText" type="text" class="input input-bordered w-full" :placeholder="t('agentStyles.placeholders.termsButtonText', 'Enter terms button text')" maxlength="40" autocomplete="off" autocapitalize="none" :spellcheck="false" />
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.placeholders.termsModalContent', 'Enter terms and conditions content') }}</span>
                    </label>
                    <textarea v-model="agentSetup.agentStyleDraft.termsModalContent" class="textarea textarea-bordered w-full h-32" :placeholder="t('agentStyles.placeholders.termsModalContent', 'Enter terms and conditions content')" maxlength="1200" autocomplete="off" autocapitalize="none" :spellcheck="false"></textarea>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="light-style-section">
            <div class="light-style-section-title">{{ t('agentStyles.tabs.clickToAction', 'Click To Action') }}</div>
            <div class="light-style-stack">
              <div class="light-style-feature-card">
                <input v-model="agentSetup.agentStyleDraft.addShadows" type="checkbox" class="checkbox checkbox-primary mt-1" />
                <div class="light-style-feature-content">
                  <div class="font-medium">{{ t('agentStyles.clickToAction.addShadows', 'Add shadows') }}</div>
                  <div class="text-sm text-gray-500 mt-1">{{ t('agentStyles.clickToAction.descriptions.addShadows', 'Adds subtle shadows to chat elements to improve depth and visibility.') }}</div>
                </div>
              </div>

              <div class="light-style-feature-card">
                <input v-model="agentSetup.agentStyleDraft.addPulse" type="checkbox" class="checkbox checkbox-primary mt-1" />
                <div class="light-style-feature-content">
                  <div class="font-medium">{{ t('agentStyles.clickToAction.addPulse', 'Add pulse') }}</div>
                  <div class="text-sm text-gray-500 mt-1">{{ t('agentStyles.clickToAction.descriptions.addPulse', 'Adds a gentle pulsing animation to the chat button to draw attention without being intrusive.') }}</div>
                </div>
              </div>

              <div class="light-style-feature-card">
                <input v-model="agentSetup.agentStyleDraft.showFloatingMessage" type="checkbox" class="checkbox checkbox-primary mt-1" />
                <div class="light-style-feature-content">
                  <div class="font-medium">{{ t('agentStyles.clickToAction.showFloatingMessage', 'Show floating message') }}</div>
                  <div class="text-sm text-gray-500 mt-1">{{ t('agentStyles.clickToAction.descriptions.showFloatingMessage', 'Displays a small floating tooltip near the chat button with a short call-to-action.') }}</div>
                  <div v-if="agentSetup.agentStyleDraft.showFloatingMessage === true" class="mt-3">
                    <label class="label">
                      <span class="label-text">{{ t('agentStyles.clickToAction.floatingMessageLabel', 'Floating Message') }}</span>
                    </label>
                    <textarea v-model="agentSetup.agentStyleDraft.floatingMessageText" class="textarea textarea-bordered w-full h-32" :placeholder="t('agentStyles.placeholders.floatingMessageText', 'Enter floating message text')" maxlength="250"></textarea>
                    <div class="text-xs text-gray-500 mt-2">{{ t('agentStyles.clickToAction.descriptions.floatingMessageText', 'Short message shown above the chat button, for example: "Need help? Ask me!"') }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <input type="radio" name="agent_style_light_tabs" role="tab" class="tab" :aria-label="t('agentStyles.tabs.css', 'CSS')" :checked="styleTab === 'css'" @click="styleTab = 'css'" />
      <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-5 md:p-6">
        <label class="label">
          <span class="label-text">{{ t('agentStyles.cssStylesLabel', 'CSS Styles') }}</span>
        </label>
        <LightIDE v-model="agentSetup.agentStyleDraft.cssstyles" />
      </div>
    </div>
    <div class="flex justify-between mt-4" style="position: relative;">
      <button @click="agentSetup.goNextStep(8, -1)" class="btn">
        <span v-if="agentSetup.isNavigating === 7" class="loading loading-spinner loading-sm mr-2"></span>
        {{ agentSetup.t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
      </button>
      <div class="flex items-center space-x-2" :class="agentSetup.isRtl === true ? 'space-x-reverse' : ''">
        <button v-if="agentSetup.isEditMode === true && agentSetup.uploadedFiles.length === 0" @click="agentSetup.saveAgentWithoutRedirect" class="btn btn-neutral">
          <span v-if="agentSetup.isSaving === true && agentSetup.isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.save', 'Save') }}
        </button>
        <button v-if="agentSetup.isEditMode === true" @click="agentSetup.createAgent(true)" class="btn">
          <span v-if="agentSetup.isSaving === true && agentSetup.isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
        </button>
        <button @click="agentSetup.goNextStep(8, 1)" class="btn">
          <span v-if="agentSetup.isNavigating === 9" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n';
import LightIDE from '@/components/LightIDE.vue';
import { useAgentSetupContext } from '@/components/AgentSetupContext.js';

const agentSetup = useAgentSetupContext();
const toast = useToast();
const { t } = useI18n();
const styleTab = ref('configuration');
const isDraggingLogo = ref(false);
const isDraggingIcon = ref(false);
const logoFileInput = ref(null);
const chatIconFileInput = ref(null);

function getFileExtension(fileName) {
  if (typeof fileName !== 'string') {
    return '';
  }
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex < 0 || lastDotIndex === fileName.length - 1) {
    return '';
  }
  return fileName.substring(lastDotIndex + 1).toLowerCase();
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function handleLogoFile(file) {
  const mimeType = typeof file.type === 'string' ? file.type.toLowerCase() : '';
  const fileExtension = getFileExtension(file.name);
  const allowedExtensions = ['svg', 'png', 'jpeg', 'jpg', 'gif'];
  const mimeAllowed = mimeType.length > 0 && mimeType.startsWith('image/');
  const extensionAllowed = allowedExtensions.includes(fileExtension) === true;
  if (mimeAllowed === false && extensionAllowed === false) {
    toast.error(t('agentStyles.toasts.imageFileRequired', 'Please upload an image file'));
    return;
  }
  if (file.size > (5 * 1024 * 1024)) {
    toast.error(t('agentStyles.toasts.imageSizeError', 'File size should not exceed 5MB'));
    return;
  }
  agentSetup.agentStyleDraft.logoType = 'image';
  agentSetup.agentStyleDraft.logoCode = '';
  agentSetup.agentStyleDraft.logobase64 = await fileToBase64(file);
}

async function handleChatIconFile(file) {
  const mimeType = typeof file.type === 'string' ? file.type.toLowerCase() : '';
  const fileExtension = getFileExtension(file.name);
  const allowedMimes = ['image/svg+xml', 'image/png', 'image/gif'];
  const allowedExtensions = ['svg', 'png', 'gif'];
  const mimeAllowed = mimeType.length > 0 && allowedMimes.includes(mimeType) === true;
  const extensionAllowed = allowedExtensions.includes(fileExtension) === true;
  if (mimeAllowed === false && extensionAllowed === false) {
    toast.error(t('agentStyles.toasts.iconFileRequired', 'Please upload an SVG, PNG, or GIF file for the icon'));
    return;
  }
  if (file.size > (1 * 1024 * 1024)) {
    toast.error(t('agentStyles.toasts.iconSizeError', 'Icon file size should not exceed 1MB'));
    return;
  }
  agentSetup.agentStyleDraft.chatIconBase64 = await fileToBase64(file);
}

async function handleLogoUpload(event) {
  isDraggingLogo.value = false;
  const file = event !== undefined && event !== null && event.target !== undefined && event.target !== null && event.target.files !== undefined ? event.target.files[0] : null;
  if (file !== null) {
    await handleLogoFile(file);
  }
}

async function handleLogoDrop(event) {
  isDraggingLogo.value = false;
  const file = event !== undefined && event !== null && event.dataTransfer !== undefined && event.dataTransfer !== null && event.dataTransfer.files !== undefined ? event.dataTransfer.files[0] : null;
  if (file !== null) {
    await handleLogoFile(file);
  }
}

async function handleChatIconUpload(event) {
  isDraggingIcon.value = false;
  const file = event !== undefined && event !== null && event.target !== undefined && event.target !== null && event.target.files !== undefined ? event.target.files[0] : null;
  if (file !== null) {
    await handleChatIconFile(file);
  }
}

async function handleChatIconDrop(event) {
  isDraggingIcon.value = false;
  const file = event !== undefined && event !== null && event.dataTransfer !== undefined && event.dataTransfer !== null && event.dataTransfer.files !== undefined ? event.dataTransfer.files[0] : null;
  if (file !== null) {
    await handleChatIconFile(file);
  }
}

function removeLogo() {
  agentSetup.agentStyleDraft.logobase64 = '';
  if (logoFileInput.value !== null) {
    logoFileInput.value.value = '';
  }
}

function removeChatIcon() {
  agentSetup.agentStyleDraft.chatIconBase64 = '';
  if (chatIconFileInput.value !== null) {
    chatIconFileInput.value.value = '';
  }
}
</script>

<style scoped>
.light-style-stack > * + * {
  margin-top: 1rem;
}

.light-style-row {
  display: flex;
  flex-direction: column;
}

.light-style-row > * + * {
  margin-top: 1rem;
}

@media (min-width: 768px) {
  .light-style-row {
    flex-direction: row;
  }

  .light-style-row > * {
    flex: 1 1 0%;
    min-width: 0;
  }

  .light-style-row > * + * {
    margin-top: 0;
    margin-left: 1rem;
  }

  html[dir="rtl"] .light-style-row > * + * {
    margin-left: 0;
    margin-right: 1rem;
  }
}

.light-style-section {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 1rem;
  background-color: rgba(0, 0, 0, 0.015);
}

.dark .light-style-section {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(255, 255, 255, 0.03);
}

.light-style-section-title {
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.light-style-color-field {
  display: flex;
  align-items: center;
}

.light-style-color-field > * + * {
  margin-left: 0.75rem;
}

html[dir="rtl"] .light-style-color-field > * + * {
  margin-left: 0;
  margin-right: 0.75rem;
}

.light-style-color-input {
  width: 52px;
  height: 48px;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: white;
  cursor: pointer;
}

.light-style-upload {
  border: 1px dashed rgba(0, 0, 0, 0.18);
  border-radius: 14px;
  min-height: 150px;
  padding: 1rem;
  cursor: pointer;
  transition: 0.2s ease;
}

.dark .light-style-upload {
  border-color: rgba(255, 255, 255, 0.16);
}

.light-style-upload:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.light-style-upload-inner {
  min-height: 116px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.light-style-upload-inner > * + * {
  margin-top: 0.5rem;
}

.light-style-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 0.75rem;
  margin-top: 0.75rem;
}

.light-style-preview-image {
  max-width: 110px;
  max-height: 56px;
  object-fit: contain;
}

.light-style-toggle-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  padding: 1rem;
  background-color: rgba(255, 255, 255, 0.55);
}

.dark .light-style-toggle-card {
  border-color: rgba(255, 255, 255, 0.12);
  background-color: rgba(255, 255, 255, 0.02);
}

.light-style-toggle-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.light-style-toggle-body {
  margin-top: 1rem;
}

.light-style-toggle-body > * + * {
  margin-top: 0.75rem;
}

.light-style-feature-card {
  display: flex;
  align-items: flex-start;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 1rem;
  transition: 0.2s ease;
}

.light-style-feature-card:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.dark .light-style-feature-card {
  border-color: rgba(255, 255, 255, 0.12);
}

.dark .light-style-feature-card:hover {
  background-color: rgba(255, 255, 255, 0.04);
}

.light-style-feature-content {
  flex: 1 1 0%;
  min-width: 0;
}

.light-style-feature-content,
.light-style-feature-card > * + * {
  margin-left: 0.75rem;
}

html[dir="rtl"] .light-style-feature-content,
html[dir="rtl"] .light-style-feature-card > * + * {
  margin-left: 0;
  margin-right: 0.75rem;
}
</style>
